/**
 * ============================================================================
 * BAĞLAMA NOKTASI — C şeridi bittiğinde değişecek TEK dosya.
 * ============================================================================
 * Arayüzün geri kalanı yalnızca `LocalRepository` tipini görür. Gerçek
 * uygulamaya geçmek için aşağıdaki `createFakeRepository(...)` çağrısını
 * `@parafon/local`'ın fabrikasıyla değiştirmek yeterlidir:
 *
 *   import { createWorkerRepository } from "@parafon/local";
 *   return createWorkerRepository();
 *
 * Sahte depo `src/dev/` altındadır ve bu dosya dışında hiçbir yerden
 * import edilmez (testler hariç).
 */

import type { LocalRepository } from "@parafon/contracts/local-api";
import type { RpcError } from "@parafon/contracts/local-api";
import { probeAppFile } from "@parafon/contracts/local-api";
import type { PriceCachePort } from "@parafon/contracts/device-api";
import { createFakeRepository } from "../dev/fakeRepository";
import { createFakePriceCache } from "../dev/fakePriceCache";
import { DEV_SEED } from "../dev/seed";
import { M } from "../dil";

const FAILURE_CODES: readonly RpcError["code"][] = [
  "LOCKED_BY_OTHER_TAB",
  "OPFS_UNAVAILABLE",
  "MIGRATION_FAILED",
  "SERVER_UNAVAILABLE",
];

/**
 * `?hata=LOCKED_BY_OTHER_TAB` ile hata ekranları elle denenebilir; `?bos=1`
 * ile boş durum ekranları görülebilir. Yalnızca geliştirme kolaylığı.
 */
function readDevFlags(search: string): { failOpenWith?: RpcError["code"]; empty: boolean } {
  const params = new URLSearchParams(search);
  const requested = params.get("hata");
  const code = FAILURE_CODES.find((c) => c === requested);
  return code === undefined ? { empty: params.has("bos") } : { failOpenWith: code, empty: params.has("bos") };
}

/**
 * GERÇEK DEPO — `packages/local`ın derlenmiş tarayıcı çıktısı.
 *
 * Neden statik dosyadan dinamik import: Worker kodu SQLite WASM'ı
 * `/vendor/sqlite-wasm/index.mjs` mutlak yolundan alıyor ve modül ağacı
 * kendi `contracts/` kopyasıyla birlikte `public/parafon-db/` altına
 * kopyalanıyor (bkz. `npm run sync:db`). Bu ağacı bundler'a sokmak
 * `.js` → `.ts` çözümleme sorunları doğurur; olduğu gibi servis etmek
 * hem daha sade hem `packages/local`ın testlerinde koşan kodun AYNISI.
 */
const DB_MODULE_URL = "/parafon-db/client.js";
const WORKER_URL = "/parafon-db/db.worker.js";

/**
 * Gerçek çekirdeğin döndürdüğü tutamaç, `LocalRepository`den FAZLASINI taşır:
 * Aşama 2'de eklenen cihaz fiyat önbelleği `prices` alanında (bkz.
 * `@parafon/contracts/device-api` — tipi sözleşmede, uygulaması çekirdekte).
 * `prices` isteğe bağlı yazıldı çünkü eski bir `sync:db` çıktısı servis
 * ediliyorsa alan GERÇEKTEN yoktur ve bunu sessizce varsaymak, ilk çağrıda
 * anlaşılmaz bir `undefined` hatası olurdu.
 */
type LocalHandle = LocalRepository & { readonly prices?: PriceCachePort };

type LocalFactory = (options: { workerUrl?: string; vfs?: "kap" }) => LocalHandle;

/** Sözleşmenin hata yüzeyi: `code`/`detail` taşıyan bir `Error`. */
function failure(error: RpcError): Error & RpcError {
  return Object.assign(new Error(error.message), error);
}

/**
 * Cihaz modülü yüklenemezse sebebi yoklama söyler (bkz. `probeAppFile`):
 * sunucu dosyayı vermediyse `SERVER_UNAVAILABLE`, verdiyse modülün kendisi
 * bozuk demektir.
 */
async function loadRealRepository(
  importModule: (url: string) => Promise<unknown>,
  fetchFn: typeof fetch,
): Promise<LocalHandle> {
  let mod: { createRepository: LocalFactory };
  try {
    mod = (await importModule(DB_MODULE_URL)) as { createRepository: LocalFactory };
  } catch (err) {
    const unavailable = await probeAppFile(DB_MODULE_URL, {
      fetchFn: (url, init) => fetchFn(url, init),
      online: globalThis.navigator?.onLine,
    });
    throw failure(
      unavailable ?? {
        code: "UNKNOWN",
        message: M.kabuk.depo.modulYuklenemedi,
        detail: err instanceof Error ? `${err.name}: ${err.message}` : String(err),
      },
    );
  }
  // KİLİTLİ DEFTER (06.10.2026): defter yalnızca şifreli kapta. Açılış
  // kilitliyse KILITLI, kurulmamışsa KURULUM_GEREKLI ile düşer; App o koda
  // göre kilit/kurulum ekranını gösterir.
  return mod.createRepository({ workerUrl: WORKER_URL, vfs: "kap" });
}

/**
 * Yükleme bir kez yapılır; BAŞARISIZ yükleme saklanmaz. Sunucu kapalıyken
 * açılan sekmede "Yeniden dene" sunucu dönünce yeniden yükler.
 */
export function retryingLoader<T>(load: () => Promise<T>): () => Promise<T> {
  let current: Promise<T> | null = null;
  return () => {
    current ??= load().catch((err: unknown) => {
      current = null;
      throw err;
    });
    return current;
  };
}

/** Gerçek yükleyici; testte modül içe aktarma ve ağ değiştirilebilir. */
export function createRealDataLayer(
  importModule: (url: string) => Promise<unknown> = (url) => import(/* @vite-ignore */ url),
  fetchFn: typeof fetch = (...args) => fetch(...args),
): DataLayer {
  const load = retryingLoader(() => loadRealRepository(importModule, fetchFn));
  return {
    repo: createLazyRepository(load),
    prices: createLazyPriceCache(load),
    cacheUnavailable: null,
  };
}

/** Depo + fiyat önbelleği: arayüzün gördüğü tek veri katmanı. */
export interface DataLayer {
  readonly repo: LocalRepository;
  /** null → bu derlemede/koşumda önbellek yok; sebebi aşağıdaki alanda. */
  readonly prices: PriceCachePort | null;
  readonly cacheUnavailable: string | null;
}

/**
 * `?sahte=1` ile sahte depoya dönülebilir — hata ve boş durum ekranlarını
 * elle denemek için. Varsayılan artık GERÇEK depo.
 */
export function createRepository(
  search: string = globalThis.location?.search ?? "",
): LocalRepository {
  return createDataLayer(search).repo;
}

export function createDataLayer(search: string = globalThis.location?.search ?? ""): DataLayer {
  const params = new URLSearchParams(search);
  const flags = readDevFlags(search);

  if (!params.has("sahte")) {
    // Gerçek depo asenkron yükleniyor; arayüz eşzamanlı bir nesne beklediği
    // için çağrıları yükleme bitene kadar kuyruğa alan ince bir vekil
    // döndürüyoruz. İlk çağrı zaten `open()` olduğu için gecikme görünmez.
    return createRealDataLayer();
  }

  return {
    repo: createFakeRepository({
      latencyMs: 120,
      ...(flags.failOpenWith === undefined ? {} : { failOpenWith: flags.failOpenWith }),
      ...(flags.empty ? {} : { seed: DEV_SEED }),
    }),
    prices: createFakePriceCache(),
    cacheUnavailable: null,
  };
}

/**
 * Söz verilmiş bir depoyu eşzamanlı arayüz arkasına saklar. Her metot
 * çağrısı önce yüklemeyi bekler, sonra gerçek metoda devreder — hata
 * biçimi (`code`/`detail`) olduğu gibi geçer.
 */
function createLazyRepository(load: () => Promise<LocalRepository>): LocalRepository {
  const on = <T>(fn: (repo: LocalRepository) => Promise<T>): Promise<T> => load().then(fn);

  // HER ALAN ZORUNLU (15.10.2026): sözleşmeye eklenen isteğe bağlı bir port
  // burada unutulursa canlıda "bu derlemede yok" çıkıyordu (sanal hesaplar).
  // Tip artık sözleşmenin bütün alanlarını istiyor: unutmak derlenmez.
  const vekil: { readonly [K in keyof Required<LocalRepository>]: NonNullable<LocalRepository[K]> } = {
    open: () => on((r) => r.open()),
    close: () => on((r) => r.close()),
    accounts: {
      list: (includeDeleted, devreDisiDahil) => on((r) => r.accounts.list(includeDeleted, devreDisiDahil)),
      get: (id) => on((r) => r.accounts.get(id)),
      create: (input) => on((r) => r.accounts.create(input)),
      update: (id, patch) => on((r) => r.accounts.update(id, patch)),
      remove: (id, hareketler) => on((r) => r.accounts.remove(id, hareketler)),
      oksuzHareketler: () => on(async (r) => (await r.accounts.oksuzHareketler?.()) ?? []),
      oksuzHareketleriCoz: (id, secim) =>
        on(async (r) => {
          const f = r.accounts.oksuzHareketleriCoz;
          if (f === undefined) throw new Error("cihaz deposu bunu desteklemiyor — `sync:db` eski olabilir");
          return f(id, secim);
        }),
    },
    transactions: {
      query: (filter) => on((r) => r.transactions.query(filter)),
      get: (id) => on((r) => r.transactions.get(id)),
      create: (input) => on((r) => r.transactions.create(input)),
      createMany: (inputs) => on((r) => r.transactions.createMany(inputs)),
      update: (id, patch) => on((r) => r.transactions.update(id, patch)),
      remove: (id) => on((r) => r.transactions.remove(id)),
    },
    manualValuations: {
      list: (label) => on((r) => r.manualValuations.list(label)),
      upsert: (input) => on((r) => r.manualValuations.upsert(input)),
      remove: (id) => on((r) => r.manualValuations.remove(id)),
    },
    manualPrices: {
      latestAsOf: (ref, date) => on((r) => r.manualPrices.latestAsOf(ref, date)),
      upsert: (price) => on((r) => r.manualPrices.upsert(price)),
      listFor: (ref) => on((r) => r.manualPrices.listFor(ref)),
      remove: (ref, date) => on((r) => r.manualPrices.remove(ref, date)),
    },
    positions: (asOf) => on((r) => r.positions(asOf)),
    valuation: (options) => on((r) => r.valuation(options)),
    valuationSeries: (options) => on((r) => r.valuationSeries(options)),
    donemselAnaliz: (istek) =>
      on((r) => {
        if (r.donemselAnaliz === undefined) throw new Error(M.kabuk.depo.donemselYok);
        return r.donemselAnaliz(istek);
      }),
    sepetDagilimi: (istek) =>
      on((r) => {
        if (r.sepetDagilimi === undefined) throw new Error(M.kabuk.depo.sepetYok);
        return r.sepetDagilimi(istek);
      }),
    getiriOzeti: (noktalar) =>
      on((r) => {
        if (r.getiriOzeti === undefined) throw new Error(M.kabuk.depo.getiriOzetiYok);
        return r.getiriOzeti(noktalar);
      }),
    olculer: (istek) =>
      on((r) => {
        if (r.olculer === undefined) throw new Error("cihaz deposu ölçüleri desteklemiyor — `sync:db` eski olabilir");
        return r.olculer(istek);
      }),
    sanalHesaplar: {
      liste: () => on((r) => sanalOf(r).liste()),
      kaydet: (girdi) => on((r) => sanalOf(r).kaydet(girdi)),
      sil: (id) => on((r) => sanalOf(r).sil(id)),
      hareketler: (id, asOf) => on((r) => sanalOf(r).hareketler(id, asOf)),
    },
    fifo: (asset) => on((r) => r.fifo(asset)),
    settings: {
      get: (key) => on((r) => r.settings.get(key)),
      set: (key, value) => on((r) => r.settings.set(key, value)),
      all: () => on((r) => r.settings.all()),
    },
    exportPlainJson: () => on((r) => r.exportPlainJson()),
    importPlainJson: (json) => on((r) => r.importPlainJson(json)),
    iceAktar: (paket, secenek) => on((r) => r.iceAktar(paket, secenek)),
    kilit: {
      durum: () => on((r) => kilitOf(r).durum()),
      kur: (parola, etiket) => on((r) => kilitOf(r).kur(parola, etiket)),
      ac: (parola, profilId) => on((r) => kilitOf(r).ac(parola, profilId)),
      acIfade: (kelimeler, yeniParola, profilId) => on((r) => kilitOf(r).acIfade(kelimeler, yeniParola, profilId)),
      etiketDegistir: (etiket) => on((r) => kilitOf(r).etiketDegistir(etiket)),
      profilSil: () => on((r) => kilitOf(r).profilSil()),
      kasaOku: () => on((r) => kilitOf(r).kasaOku()),
      kasaYaz: (metin) => on((r) => kilitOf(r).kasaYaz(metin)),
      parolaDegistir: (yeni) => on((r) => kilitOf(r).parolaDegistir(yeni)),
      ifadeYenile: () => on((r) => kilitOf(r).ifadeYenile()),
      // Eski Worker bilmiyorsa: doğrulanamadı = false değil, hata (şart sessizce geçilmesin).
      yedektenKur: (metin, sir, etiket) =>
        on(async (r) => {
          const k = kilitOf(r);
          if (k.yedektenKur === undefined) throw new Error(M.kabuk.depo.yedektenKurYok);
          return k.yedektenKur(metin, sir, etiket);
        }),
      parolaDogrula: (parola) =>
        on(async (r) => {
          const k = kilitOf(r);
          if (k.parolaDogrula === undefined) throw new Error(M.kabuk.depo.parolaDogrulaYok);
          return k.parolaDogrula(parola);
        }),
      yaz: () => on((r) => kilitOf(r).yaz()),
      bosDefterleBasla: () => on((r) => kilitOf(r).bosDefterleBasla()),
      // Eski Worker (önbellekte kalmış `sync:db`) bilmiyorsa sessizce hiçbir şey:
      // oturum yalnızca bir kolaylık, kilit kendi işini yapmaya devam eder.
      oturumuSurdur: (sureDk) => on(async (r) => kilitOf(r).oturumuSurdur?.(sureDk)),
      oturumuUnut: () => on(async (r) => kilitOf(r).oturumuUnut?.()),
    },
    seriOnbellegi: {
      oku: (para, from, to) => on((r) => seriOf(r).oku(para, from, to)),
      yaz: (para, points) => on((r) => seriOf(r).yaz(para, points)),
      sil: (from) => on((r) => seriOf(r).sil(from)),
    },
    sifreliYedek: {
      olustur: () => on((r) => yedekOf(r).olustur()),
      geriYukle: (metin, sir) => on((r) => yedekOf(r).geriYukle(metin, sir)),
      dosyaOneki: () => on((r) => yedekOf(r).dosyaOneki()),
      defteriDegistir: (metin, sir) => on((r) => yedekOf(r).defteriDegistir(metin, sir)),
      defterParcasi: () => on((r) => yedekOf(r).defterParcasi()),
      esitle: (metin) => on((r) => yedekOf(r).esitle(metin)),
      aitMi: (metin) => on((r) => yedekOf(r).aitMi(metin)),
      esitlemeIziniIlerlet: () => on((r) => yedekOf(r).esitlemeIziniIlerlet()),
    },
  };
  return vekil;
}

function sanalOf(r: LocalRepository): NonNullable<LocalRepository["sanalHesaplar"]> {
  if (r.sanalHesaplar === undefined) {
    throw new Error("cihaz deposu sanal hesapları desteklemiyor — `sync:db` eski olabilir");
  }
  return r.sanalHesaplar;
}

/** Eski bir `sync:db` çıktısında kilit yoksa SESSİZ KALMAZ. */
function kilitOf(r: LocalRepository): NonNullable<LocalRepository["kilit"]> {
  if (r.kilit === undefined) throw new Error("cihaz deposu kilit desteklemiyor — `sync:db` eski olabilir");
  return r.kilit;
}

function seriOf(r: LocalRepository): NonNullable<LocalRepository["seriOnbellegi"]> {
  if (r.seriOnbellegi === undefined) throw new Error("cihaz deposu seri önbelleği desteklemiyor — `sync:db` eski olabilir");
  return r.seriOnbellegi;
}

function yedekOf(r: LocalRepository): NonNullable<LocalRepository["sifreliYedek"]> {
  if (r.sifreliYedek === undefined) {
    throw new Error("cihaz deposu şifreli yedek desteklemiyor — `sync:db` eski olabilir");
  }
  return r.sifreliYedek;
}

/**
 * Fiyat önbelleğinin eşzamanlı vekili. Eksik `prices` alanı SESSİZ KALMAZ:
 * çağrı, ne yapılması gerektiğini söyleyen bir hatayla düşer ve ekran onu
 * hata bloğunda gösterir.
 */
function createLazyPriceCache(load: () => Promise<LocalHandle>): PriceCachePort {
  const on = <T>(fn: (cache: PriceCachePort) => Promise<T>): Promise<T> =>
    load().then((handle) => {
      if (handle.prices === undefined) {
        throw new Error(
          "cihaz fiyat önbelleği bu derlemede yok — `npm -w @parafon/web run sync:db` çalıştırılmalı",
        );
      }
      return fn(handle.prices);
    });

  return {
    putBars: (id, source, bars, coverage) => on((c) => c.putBars(id, source, bars, coverage)),
    putFx: (source, rates) => on((c) => c.putFx(source, rates)),
    putSpot: (quotes) => on((c) => c.putSpot(quotes)),
    putCorporateActions: (series, from, to) => on((c) => c.putCorporateActions(series, from, to)),
    corporateActions: (id) => on((c) => c.corporateActions(id)),
    readFx: (base, quote, from, to) => on((c) => c.readFx(base, quote, from, to)),
    closeAsOf: (id, date) => on((c) => c.closeAsOf(id, date)),
    fxAsOf: (base, quote, date) => on((c) => c.fxAsOf(base, quote, date)),
    missingRanges: (id, from, to) => on((c) => c.missingRanges(id, from, to)),
    fetchPlan: (id, from, to) => on((c) => c.fetchPlan(id, from, to)),
    putInstrument: (input) => on((c) => c.putInstrument(input)),
    instrumentState: (id) => on((c) => c.instrumentState(id)),
    instrumentStates: (ids) => on((c) => c.instrumentStates(ids)),
    putGaps: (id, gaps, reason) => on((c) => c.putGaps(id, gaps, reason)),
    touch: (ids, now) => on((c) => c.touch(ids, now)),
    cacheStats: () => on((c) => c.cacheStats()),
    prune: (options) => on((c) => c.prune(options)),
    clear: () => on((c) => c.clear()),
  };
}

/** Artık gerçek depo varsayılan; sahte yalnızca `?sahte=1` ile geliyor. */
export const REPOSITORY_IS_FAKE = false;
